import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getFleetRiskForecast } from '@/services/api';
import type { FleetRiskForecast, NodeRiskScore } from '@/types/metrics';

const RISK_COLORS: Record<string, string> = {
  critical: 'dls-text-status-offline dls-bg-status-offline-quarternary',
  high: 'dls-text-status-warning dls-bg-status-warning-quarternary',
  medium: 'dls-text-status-warning dls-bg-status-warning-quarternary',
  low: 'dls-text-status-info dls-bg-status-info/15',
};

function RiskBar({ score }: { score: number }) {
  const color = score >= 70 ? 'dls-bg-status-offline' : score >= 45 ? 'dls-bg-status-warning' : score >= 25 ? 'dls-bg-status-warning-secondary' : 'dls-bg-status-info';
  return (
    <div className="dls-w-16 dls-h-2 dls-rounded-full dls-bg-muted dls-overflow-hidden">
      <div className={`dls-h-full dls-rounded-full ${color}`} style={{ width: `${score}%` }} />
    </div>
  );
}

function SignalTooltip({ signals }: { signals: NodeRiskScore['signals'] }) {
  return (
    <div className="dls-text-xs dls-space-y-0.5">
      {signals
        .filter((s) => s.value > 0)
        .map((s) => (
          <div key={s.signal} className="dls-flex dls-justify-between dls-gap-3">
            <span className="dls-text-muted-foreground">{s.signal}:</span>
            <span>{s.detail}</span>
          </div>
        ))}
    </div>
  );
}

export function NodesAtRiskCard() {
  const [data, setData] = useState<FleetRiskForecast | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getFleetRiskForecast();
        if (!cancelled) setData(result);
      } catch {
        // Silently fail – widget is informational
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !data || data.totalAtRisk === 0) return null;

  return (
    <Card className="dls-h-full dls-flex dls-flex-col">
      <CardHeader className="dls-pb-2 dls-shrink-0">
        <div className="dls-flex dls-items-center dls-justify-between">
          <CardTitle className="dls-flex dls-items-center dls-gap-2 dls-text-base">
            <AlertTriangle className="dls-h-4 dls-w-4 dls-text-status-warning" />
            Nodes at Risk
            <span className="dls-text-xs dls-font-normal dls-text-muted-foreground dls-ml-1">
              ({data.totalAtRisk} flagged)
            </span>
          </CardTitle>
          <Link
            to="/triage"
            className="dls-text-xs dls-text-muted-foreground hover:dls-text-foreground dls-flex dls-items-center dls-gap-1"
          >
            Triage <ChevronRight className="dls-h-3 dls-w-3" />
          </Link>
        </div>
        {(data.criticalCount > 0 || data.highCount > 0) && (
          <div className="dls-flex dls-gap-3 dls-mt-1">
            {data.criticalCount > 0 && (
              <span className="dls-text-xs dls-font-medium dls-text-status-offline">
                {data.criticalCount} critical
              </span>
            )}
            {data.highCount > 0 && (
              <span className="dls-text-xs dls-font-medium dls-text-status-warning">
                {data.highCount} high risk
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent className="dls-pt-0 dls-flex-1 dls-overflow-y-auto dls-min-h-0">
        <div className="dls-space-y-2">
          {data.nodesAtRisk.map((node) => (
            <div key={node.nodeId} className="dls-flex dls-items-center dls-gap-3 dls-group">
              <span
                className={`dls-text-[10px] dls-font-bold dls-px-1.5 dls-py-0.5 dls-rounded ${RISK_COLORS[node.riskLevel]}`}
              >
                {node.riskScore}
              </span>
              <Link
                to={`/nodes/${node.nodeId}`}
                className="dls-flex-1 dls-min-w-0 dls-text-sm dls-truncate hover:dls-underline"
                title={node.nodeName}
              >
                {node.nodeName}
              </Link>
              {node.portfolio && (
                <span className="dls-text-xs dls-text-muted-foreground dls-hidden sm:dls-block dls-truncate dls-max-w-[100px]">
                  {node.portfolio}
                </span>
              )}
              <RiskBar score={node.riskScore} />
              <div className="dls-opacity-0 group-hover:dls-opacity-100 dls-transition-opacity">
                <div className="dls-relative dls-group/tip">
                  <span className="dls-text-xs dls-text-muted-foreground dls-cursor-help">?</span>
                  <div className="dls-absolute dls-right-0 dls-bottom-6 dls-w-56 dls-bg-popover dls-border dls-rounded-md dls-shadow-40 dls-p-2 dls-hidden group-hover/tip:dls-block dls-z-10">
                    <SignalTooltip signals={node.signals} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
